import { CategoryRow } from 'src/data-capture/file-management/crossEntityFolders';
import { UploadedFile } from 'src/data-capture/hooks/file-uploads/types';

export type FileTreeNode = {
  record: UploadedFile | CategoryRow;
  depth: number;
  children: FileTreeNode[];
};

export type FlatRow = {
  record: UploadedFile | CategoryRow;
  depth: number;
  hasChildren: boolean;
  isExpanded: boolean;
};

/**
 * A category entry passed to buildFileTree.
 *
 * `files` must be a scoped, resolved/filed flat list. Do NOT pass raw
 * buildFamilyRootRecords / buildClientRootRecords output here — those arrays
 * mix real UploadedFiles with synthetic CategoryRows and must not feed
 * useFiledDocumentsView or buildFileTree.
 */
export type CategoryEntry = {
  row: CategoryRow;
  files: UploadedFile[];
};

function nodeId(record: UploadedFile | CategoryRow): string {
  return record.id;
}

function sortRecords(records: (UploadedFile | CategoryRow)[]): (UploadedFile | CategoryRow)[] {
  const folders = records.filter(r => r.isFolder);
  const documents = records
    .filter(r => !r.isFolder)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  return [...folders, ...documents];
}

function buildSubtree(
  files: UploadedFile[],
  parentId: string | null,
  depth: number,
  folderIndex: Map<string, UploadedFile>,
  visitedAncestors: Set<string>,
  placedIds: Set<string>,
): FileTreeNode[] {
  const children = files.filter(f => {
    const fp = f.parentId ?? null;
    if (fp !== parentId) return false;
    if (fp === null) return true;

    const parent = folderIndex.get(fp);
    if (!parent) return false;
    return true;
  });

  const sorted = sortRecords(children);

  return sorted.flatMap(record => {
    const id = nodeId(record);

    if (visitedAncestors.has(id)) {
      return [];
    }

    placedIds.add(id);

    const nextVisited = new Set(visitedAncestors);
    nextVisited.add(id);

    const nodeChildren = record.isFolder
      ? buildSubtree(files, id, depth + 1, folderIndex, nextVisited, placedIds)
      : [];

    return [{ record, depth, children: nodeChildren }];
  });
}

/**
 * Surfaces orphans and cycle members at root exactly once — no record is ever silently dropped.
 */
function surfaceUnplaced(
  files: UploadedFile[],
  folderIndex: Map<string, UploadedFile>,
  placedIds: Set<string>,
  depth: number,
): FileTreeNode[] {
  const result: FileTreeNode[] = [];

  // Check placedIds lazily inside the loop: processing one record's subtree may
  // place others that must then be skipped to avoid duplicate root nodes.
  for (const record of sortRecords(files)) {
    if (placedIds.has(record.id)) continue;

    placedIds.add(record.id);
    result.push({
      record,
      depth,
      children: record.isFolder
        ? buildSubtree(files, record.id, depth + 1, folderIndex, new Set([record.id]), placedIds)
        : [],
    });
  }

  return result;
}

function mergeAndSort(
  primary: FileTreeNode[],
  extra: FileTreeNode[],
  depth: number,
): FileTreeNode[] {
  const nodeByRecord = new Map<UploadedFile | CategoryRow, FileTreeNode>();
  for (const n of primary) nodeByRecord.set(n.record, n);
  for (const n of extra) nodeByRecord.set(n.record, n);

  const allRecords = sortRecords([...primary.map(n => n.record), ...extra.map(n => n.record)]);

  return allRecords.map(record => nodeByRecord.get(record) ?? { record, depth, children: [] });
}

export function buildFileTree(files: UploadedFile[], categories?: CategoryEntry[]): FileTreeNode[] {
  const folderIndex = new Map<string, UploadedFile>(
    files.filter(f => f.isFolder).map(f => [f.id, f]),
  );

  const placedIds = new Set<string>();

  const ownRootNodes = buildSubtree(files, null, 0, folderIndex, new Set(), placedIds);

  const unplacedNodes = surfaceUnplaced(files, folderIndex, placedIds, 0);

  const ownAndUnplaced = mergeAndSort(ownRootNodes, unplacedNodes, 0);

  if (!categories || categories.length === 0) {
    return ownAndUnplaced;
  }

  const categoryNodes: FileTreeNode[] = categories.map(({ row, files: catFiles }) => {
    const catFolderIndex = new Map<string, UploadedFile>(
      catFiles.filter(f => f.isFolder).map(f => [f.id, f]),
    );
    const catPlacedIds = new Set<string>([row.id]);
    const catChildren = buildSubtree(
      catFiles,
      null,
      1,
      catFolderIndex,
      new Set([row.id]),
      catPlacedIds,
    );

    const catUnplaced = surfaceUnplaced(catFiles, catFolderIndex, catPlacedIds, 1);

    const allCatChildren = mergeAndSort(catChildren, catUnplaced, 1);

    return {
      record: row,
      depth: 0,
      children: allCatChildren,
    };
  });

  return [...ownAndUnplaced, ...categoryNodes];
}

export function initialExpandedIds(tree: FileTreeNode[]): Set<string> {
  const ids = new Set<string>();
  for (const node of tree) {
    if (node.depth !== 0) continue;
    if (node.record.isFolder) {
      ids.add(nodeId(node.record));
    }
  }
  return ids;
}

export function flattenVisible(tree: FileTreeNode[], expandedIds: Set<string>): FlatRow[] {
  const result: FlatRow[] = [];

  function walk(nodes: FileTreeNode[]): void {
    for (const node of nodes) {
      const id = nodeId(node.record);
      const hasChildren = node.children.length > 0;
      const isExpanded = expandedIds.has(id);

      result.push({
        record: node.record,
        depth: node.depth,
        hasChildren,
        isExpanded,
      });

      if (isExpanded && hasChildren) {
        walk(node.children);
      }
    }
  }

  walk(tree);
  return result;
}

/**
 * Returns ids of matching nodes plus all their ancestors, or null when term is empty.
 */
export function filterTreeIds(tree: FileTreeNode[], term: string): Set<string> | null {
  if (!term) return null;

  const matchingIds = new Set<string>();
  const ancestorIds = new Set<string>();

  function walkCollectMatches(nodes: FileTreeNode[], ancestors: string[]): boolean {
    let anyMatch = false;
    for (const node of nodes) {
      const id = nodeId(node.record);
      const fileName = node.record.file.fileName.toLowerCase();
      const selfMatches = fileName.includes(term);

      const childAncestors = [...ancestors, id];
      const childHasMatch = walkCollectMatches(node.children, childAncestors);

      if (selfMatches) {
        matchingIds.add(id);
        for (const anc of ancestors) {
          ancestorIds.add(anc);
        }
        anyMatch = true;
      }

      if (childHasMatch) {
        ancestorIds.add(id);
        anyMatch = true;
      }
    }
    return anyMatch;
  }

  walkCollectMatches(tree, []);

  const result = new Set<string>([...matchingIds, ...ancestorIds]);
  return result;
}
